import { League } from '@/src/domain/models';
import { fontSize, fontWeight, letterSpacing } from '@/src/core/utils/typography';
import { useTranslation } from '@/src/i18n';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

export interface LeagueCardProps {
  league: League;
  tournamentCount?: number;
  participantCount?: number;
  onPress: () => void;
  onMenuPress?: (event: any) => void;
  colors: {
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    divider: string;
    badgeSetup: string;
    badgeProgress: string;
    badgeCompleted: string;
    shadow: string;
  };
}

export const LeagueCard = ({
  league,
  tournamentCount,
  participantCount,
  onPress,
  onMenuPress,
  colors,
}: LeagueCardProps) => {
  const { t } = useTranslation();

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'setup':
        return t('leagues.setup');
      case 'active':
        return t('leagues.active');
      case 'completed':
        return t('leagues.completed');
      default:
        return status;
    }
  };

  return (
    <TouchableOpacity
      style={[styles.item, { backgroundColor: colors.card, borderColor: colors.border, shadowColor: colors.shadow }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.itemHeader}>
        <View style={styles.nameRow}>
          <View style={[styles.logoCircle, { backgroundColor: colors.border }]}>
            {league.logoUrl ? (
              <Image source={{ uri: league.logoUrl }} style={styles.logoImage} />
            ) : (
              <MaterialCommunityIcons name="trophy-outline" size={18} color={colors.text} />
            )}
          </View>
          <Text style={[styles.itemName, { color: colors.text }]}>{league.name}</Text>
        </View>
        <View style={styles.itemRight}>
          <View
            style={[
              styles.badge,
              {
                backgroundColor:
                  league.status === 'completed'
                    ? colors.badgeCompleted
                    : league.status === 'active'
                      ? colors.badgeProgress
                      : colors.badgeSetup,
              },
            ]}
          >
            <Text style={[styles.badgeText, { color: colors.text }]}>{getStatusLabel(league.status)}</Text>
          </View>
          {onMenuPress && (
            <TouchableOpacity style={styles.menuBtn} onPress={onMenuPress}>
              <Ionicons name="ellipsis-vertical" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      </View>
      {league.description && (
        <Text style={[styles.itemDesc, { color: colors.textSecondary }]} numberOfLines={2}>
          {league.description}
        </Text>
      )}
      <View style={[styles.itemInfo, { borderTopColor: colors.divider }]}>
        {tournamentCount !== undefined && (
          <View style={styles.infoRow}>
            <MaterialCommunityIcons name="tournament" size={14} color={colors.textTertiary} />
            <Text style={[styles.infoText, { color: colors.textTertiary }]}>
              {tournamentCount} {t('leagues.tournaments').toLowerCase()}
            </Text>
          </View>
        )}
        {participantCount !== undefined && (
          <View style={styles.infoRow}>
            <Ionicons name="people-outline" size={14} color={colors.textTertiary} />
            <Text style={[styles.infoText, { color: colors.textTertiary }]}>
              {participantCount} {t('leagues.participants').toLowerCase()}
            </Text>
          </View>
        )}
        <View style={styles.infoRow}>
          <Ionicons name="calendar-outline" size={12} color={colors.textTertiary} />
          <Text style={[styles.infoText, { color: colors.textTertiary }]}>
            {new Date(league.createdAt).toLocaleDateString()}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  item: {
    marginHorizontal: 15,
    marginVertical: 8,
    padding: 15,
    borderRadius: 12,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  logoCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  logoImage: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  itemName: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.tight,
    flex: 1,
  },
  itemRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.wide,
  },
  menuBtn: {
    padding: 4,
    paddingHorizontal: 8,
  },
  itemDesc: {
    fontSize: fontSize.base,
    lineHeight: 20,
    marginBottom: 8,
  },
  itemInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  infoText: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
  },
});
